/**
 * Workspace Settings Page — Premium Redesign
 * 
 * Comprehensive settings management with:
 * - General settings (name, description, ID, created)
 * - Members management (invite, role change, remove)
 * - Convert to Organization flow
 * - Danger zone (transfer ownership, delete)
 * - Full i18n + light/dark mode
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSave,
  faTrash,
  faUsers,
  faCog,
  faExclamationTriangle,
  faUserPlus,
  faBuilding,
  faUser,
  faCheck,
  faTimes,
  faExchangeAlt,
  faCrown,
  faShieldAlt,
  faBox,
  faTag,
  faCopy,
  faCalendar,
  faLayerGroup,
  faInfoCircle,
  faRocket,
} from '@fortawesome/free-solid-svg-icons';
import { workspacesAPI } from '../services/api';
import { useRBAC, useWorkspaces, useWorkspaceMembers, useWorkspaceInvitations } from '../hooks/useRBAC';
import { WorkspaceRole, WORKSPACE_ROLE_DISPLAY_NAMES, Permission } from '../types/rbac';
import { useI18n } from '../hooks/useI18n';
import '../styles/WorkspaceSettings.css';

interface WorkspaceData {
  id: string;
  name: string;
  description?: string;
  type: 'PERSONAL' | 'ORGANIZATION';
  createdAt: string;
  ownerId: string;
  _count?: {
    items: number;
    categories: number;
    members?: number;
  };
}

const ROLE_COLORS: Record<string, string> = {
  OWNER: '#fbbf24',
  ORG_ADMIN: '#f472b6',
  MANAGER: '#667eea',
  REVIEWER: '#34d399',
  VIEWER: '#60a5fa',
  GUEST: '#a78bfa',
};

const WorkspaceSettings = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { currentWorkspace, hasPermission, workspaceRole, refresh: refreshRBAC } = useRBAC();
  const { convertToOrganization } = useWorkspaces();

  const workspaceId = id || currentWorkspace?.id;

  const { members, loading: membersLoading, changeRole, removeMember, transferOwnership } = useWorkspaceMembers(workspaceId);
  const { invitations, inviteMember, revokeInvitation } = useWorkspaceInvitations(workspaceId);

  const [workspace, setWorkspace] = useState<WorkspaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>('VIEWER');
  const [inviting, setInviting] = useState(false);

  // Transfer ownership
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferUserId, setTransferUserId] = useState('');
  const [transferring, setTransferring] = useState(false);

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Convert to org
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);

  // Messages
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState(false);

  const fetchWorkspace = useCallback(async () => {
    if (!workspaceId) return;
    try {
      setLoading(true);
      const response = await workspacesAPI.getById(workspaceId);
      const data = response.data?.workspace ?? response.data;
      setWorkspace(data);
      setName(data.name);
      setDescription(data.description || '');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { fetchWorkspace(); }, [fetchWorkspace]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleSaveGeneral = async () => {
    if (!workspaceId || !name.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await workspacesAPI.update(workspaceId, {
        name: name.trim(),
        description: description.trim() || undefined,
      });
      setSuccess(t('wsSettings.savedSuccess'));
      await refreshRBAC();
      fetchWorkspace();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) return;
    try {
      setInviting(true);
      setError(null);
      await inviteMember(inviteEmail.trim(), inviteRole);
      setInviteEmail('');
      setInviteRole('VIEWER');
      setSuccess(t('wsSettings.inviteSent'));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleChangeRole = async (userId: string, newRole: WorkspaceRole) => {
    try {
      setError(null);
      await changeRole(userId, newRole);
      setSuccess(t('wsSettings.roleUpdated'));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!window.confirm(t('wsSettings.confirmRemoveMember'))) return;
    try {
      setError(null);
      await removeMember(userId);
      setSuccess(t('wsSettings.memberRemoved'));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      setError(null);
      await revokeInvitation(invitationId);
      setSuccess(t('wsSettings.inviteRevoked'));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revoke invitation');
    }
  };

  const handleTransferOwnership = async () => {
    if (!transferUserId) return;
    try {
      setTransferring(true);
      setError(null);
      await transferOwnership(transferUserId);
      setShowTransferModal(false);
      setTransferUserId('');
      setSuccess(t('wsSettings.ownershipTransferred'));
      await refreshRBAC();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to transfer ownership');
    } finally {
      setTransferring(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (deleteConfirmName !== workspace?.name) {
      setError(t('wsSettings.typeNameToConfirm'));
      return;
    }
    try {
      setDeleting(true);
      setError(null);
      await workspacesAPI.delete(workspaceId!);
      await refreshRBAC();
      navigate('/workspaces');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete workspace');
      setDeleting(false);
    }
  };

  const handleConvertToOrg = async () => {
    if (!workspaceId) return;
    try {
      setConverting(true);
      setError(null);
      await convertToOrganization(workspaceId);
      setShowConvertModal(false);
      setSuccess(t('wsSettings.convertedSuccess'));
      await refreshRBAC();
      fetchWorkspace();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to convert');
    } finally {
      setConverting(false);
    }
  };

  const copyId = () => {
    if (workspace) {
      navigator.clipboard.writeText(workspace.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  const isOwner = workspaceRole === 'OWNER';
  const isAdmin = isOwner || workspaceRole === 'ORG_ADMIN';
  const canManageMembers = isAdmin || hasPermission(Permission.MEMBER_INVITE);
  const isOrg = workspace?.type === 'ORGANIZATION';

  const getAssignableRoles = (): WorkspaceRole[] => {
    if (isOwner) return ['ORG_ADMIN', 'MANAGER', 'REVIEWER', 'VIEWER', 'GUEST'];
    if (workspaceRole === 'ORG_ADMIN') return ['MANAGER', 'REVIEWER', 'VIEWER', 'GUEST'];
    return ['VIEWER', 'GUEST'];
  };

  if (loading) {
    return (
      <div className="wss-page">
        <div className="wss-loading">
          <div className="wss-spinner" />
          <p>{t('wsSettings.loading')}</p>
        </div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="wss-page">
        <div className="wss-error-state">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <h3>{error || t('wsSettings.notFound')}</h3>
          <button className="wss-btn-primary" onClick={() => navigate('/workspaces')}>
            {t('wsSettings.backToWorkspaces')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="wss-page">
      {/* Toast Messages */}
      {error && (
        <div className="wss-toast wss-toast-error">
          <FontAwesomeIcon icon={faTimes} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {success && (
        <div className="wss-toast wss-toast-success">
          <FontAwesomeIcon icon={faCheck} />
          <span>{success}</span>
        </div>
      )}

      {/* Header */}
      <div className="wss-header">
        <button className="wss-back-btn" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className={`wss-ws-icon ${isOrg ? 'org' : 'personal'}`}>
          <FontAwesomeIcon icon={isOrg ? faBuilding : faUser} />
        </div>
        <div className="wss-header-text">
          <h1>{workspace.name}</h1>
          <span className="wss-ws-type">
            {isOrg ? t('wsSettings.organization') : t('wsSettings.personalWorkspace')}
            {isOwner && <span className="wss-owner-tag"><FontAwesomeIcon icon={faCrown} /> {t('wsSettings.owner')}</span>}
          </span>
        </div>
      </div>

      {/* Settings Layout */}
      <div className="wss-layout">
        {/* Sidebar Tabs */}
        <nav className="wss-nav">
          <button className={`wss-nav-item ${activeTab === 'general' ? 'active' : ''}`} onClick={() => setActiveTab('general')}>
            <FontAwesomeIcon icon={faCog} />
            <span>{t('wsSettings.general')}</span>
          </button>
          {isOrg && (
            <button className={`wss-nav-item ${activeTab === 'members' ? 'active' : ''}`} onClick={() => setActiveTab('members')}>
              <FontAwesomeIcon icon={faUsers} />
              <span>{t('wsSettings.members')}</span>
              <span className="wss-nav-badge">{members.length}</span>
            </button>
          )}
          {!isOrg && isOwner && (
            <button className={`wss-nav-item ${activeTab === 'convert' ? 'active' : ''}`} onClick={() => setActiveTab('convert')}>
              <FontAwesomeIcon icon={faBuilding} />
              <span>{t('wsSettings.convertTab')}</span>
            </button>
          )}
          {isOwner && (
            <button className={`wss-nav-item wss-nav-danger ${activeTab === 'danger' ? 'active' : ''}`} onClick={() => setActiveTab('danger')}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{t('wsSettings.dangerZone')}</span>
            </button>
          )}
        </nav>

        {/* Content */}
        <div className="wss-content">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="wss-section">
              <div className="wss-section-head">
                <FontAwesomeIcon icon={faCog} />
                <h2>{t('wsSettings.generalSettings')}</h2>
              </div>

              <div className="wss-form-group">
                <label>{t('wsSettings.workspaceName')}</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('wsSettings.enterName')}
                  disabled={!isAdmin}
                />
              </div>

              <div className="wss-form-group">
                <label>{t('wsSettings.description')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('wsSettings.descriptionPlaceholder')}
                  rows={3}
                  disabled={!isAdmin}
                />
              </div>

              <div className="wss-info-grid">
                <div className="wss-info-card">
                  <div className="wss-info-icon"><FontAwesomeIcon icon={faCopy} /></div>
                  <div className="wss-info-data">
                    <label>{t('wsSettings.workspaceId')}</label>
                    <div className="wss-copy-row">
                      <code>{workspace.id.substring(0, 16)}...</code>
                      <button onClick={copyId} title="Copy ID">
                        <FontAwesomeIcon icon={copiedId ? faCheck : faCopy} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="wss-info-card">
                  <div className="wss-info-icon"><FontAwesomeIcon icon={faCalendar} /></div>
                  <div className="wss-info-data">
                    <label>{t('wsSettings.created')}</label>
                    <span>{new Date(workspace.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {workspace._count && (
                <div className="wss-stats">
                  <div className="wss-stat">
                    <FontAwesomeIcon icon={faBox} />
                    <span className="wss-stat-val">{workspace._count.items}</span>
                    <span className="wss-stat-lbl">{t('wsSettings.items')}</span>
                  </div>
                  <div className="wss-stat">
                    <FontAwesomeIcon icon={faTag} />
                    <span className="wss-stat-val">{workspace._count.categories}</span>
                    <span className="wss-stat-lbl">{t('wsSettings.categories')}</span>
                  </div>
                  {isOrg && (
                    <div className="wss-stat">
                      <FontAwesomeIcon icon={faUsers} />
                      <span className="wss-stat-val">{members.length}</span>
                      <span className="wss-stat-lbl">{t('wsSettings.members')}</span>
                    </div>
                  )}
                </div>
              )}

              {isAdmin && (
                <button className="wss-btn-primary wss-save-btn" onClick={handleSaveGeneral} disabled={saving || !name.trim()}>
                  <FontAwesomeIcon icon={faSave} />
                  {saving ? t('wsSettings.saving') : t('wsSettings.saveChanges')}
                </button>
              )}
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && isOrg && (
            <div className="wss-section">
              <div className="wss-section-head">
                <FontAwesomeIcon icon={faUsers} />
                <h2>{t('wsSettings.members')}</h2>
              </div>

              {/* Invite */}
              {canManageMembers && (
                <div className="wss-invite-card">
                  <h3><FontAwesomeIcon icon={faUserPlus} /> {t('wsSettings.inviteMember')}</h3>
                  <div className="wss-invite-row">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder={t('wsSettings.emailPlaceholder')}
                    />
                    <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as WorkspaceRole)}>
                      {getAssignableRoles().map(r => (
                        <option key={r} value={r}>{WORKSPACE_ROLE_DISPLAY_NAMES[r]}</option>
                      ))}
                    </select>
                    <button className="wss-btn-primary" onClick={handleInviteMember} disabled={inviting || !inviteEmail.trim()}>
                      {inviting ? '...' : t('wsSettings.sendInvite')}
                    </button>
                  </div>
                </div>
              )}

              {/* Pending Invitations */}
              {invitations.length > 0 && (
                <div className="wss-subsection">
                  <h3>{t('wsSettings.pendingInvitations')} ({invitations.length})</h3>
                  <div className="wss-list">
                    {invitations.map((inv: any) => (
                      <div key={inv.id} className="wss-inv-row">
                        <div className="wss-inv-avatar">
                          <FontAwesomeIcon icon={faUserPlus} />
                        </div>
                        <div className="wss-inv-info">
                          <span className="wss-inv-email">{inv.email}</span>
                          <span className="wss-inv-role" style={{ color: ROLE_COLORS[inv.role] }}>
                            {WORKSPACE_ROLE_DISPLAY_NAMES[inv.role as WorkspaceRole]}
                          </span>
                        </div>
                        {canManageMembers && (
                          <button className="wss-icon-btn danger" onClick={() => handleRevokeInvitation(inv.id)} title={t('wsSettings.revoke')}>
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Members */}
              <div className="wss-subsection">
                <h3>{t('wsSettings.currentMembers')} ({members.length})</h3>
                {membersLoading ? (
                  <div className="wss-loading-inline">{t('wsSettings.loadingMembers')}</div>
                ) : (
                  <div className="wss-list">
                    {members.map((member: any) => {
                      const memberName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim() || member.user?.email || '?';
                      const roleColor = ROLE_COLORS[member.role] || '#667eea';

                      return (
                        <div key={member.id} className="wss-member-row">
                          <div className="wss-member-avatar" style={{ borderColor: roleColor }}>
                            {memberName.charAt(0).toUpperCase()}
                          </div>
                          <div className="wss-member-info">
                            <span className="wss-member-name">
                              {memberName}
                              {member.role === 'OWNER' && <FontAwesomeIcon icon={faCrown} style={{ color: '#fbbf24', marginLeft: 6, fontSize: 11 }} />}
                            </span>
                            <span className="wss-member-email">{member.user?.email}</span>
                          </div>
                          <div className="wss-member-actions">
                            {canManageMembers && member.role !== 'OWNER' ? (
                              <>
                                <select
                                  value={member.role}
                                  onChange={(e) => handleChangeRole(member.userId || member.user?.id, e.target.value as WorkspaceRole)}
                                  className="wss-role-select"
                                  style={{ borderColor: `${roleColor}40` }}
                                >
                                  {getAssignableRoles().map(r => (
                                    <option key={r} value={r}>{WORKSPACE_ROLE_DISPLAY_NAMES[r]}</option>
                                  ))}
                                </select>
                                <button className="wss-icon-btn danger" onClick={() => handleRemoveMember(member.userId || member.user?.id)} title={t('wsSettings.remove')}>
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </>
                            ) : (
                              <span className="wss-role-badge owner">
                                <FontAwesomeIcon icon={faCrown} /> {t('wsSettings.owner')}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Convert Tab (personal only) */}
          {activeTab === 'convert' && !isOrg && isOwner && (
            <div className="wss-section">
              <div className="wss-section-head">
                <FontAwesomeIcon icon={faBuilding} />
                <h2>{t('wsSettings.convertToOrg')}</h2>
              </div>

              <div className="wss-convert-promo">
                <div className="wss-convert-promo-icon">
                  <FontAwesomeIcon icon={faRocket} />
                </div>
                <h3>{t('wsSettings.upgradeTitle')}</h3>
                <p>{t('wsSettings.upgradeDesc')}</p>

                <div className="wss-convert-features">
                  <div className="wss-convert-feat">
                    <FontAwesomeIcon icon={faUserPlus} />
                    <div>
                      <strong>{t('wsSettings.featInvite')}</strong>
                      <span>{t('wsSettings.featInviteDesc')}</span>
                    </div>
                  </div>
                  <div className="wss-convert-feat">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    <div>
                      <strong>{t('wsSettings.featRBAC')}</strong>
                      <span>{t('wsSettings.featRBACDesc')}</span>
                    </div>
                  </div>
                  <div className="wss-convert-feat">
                    <FontAwesomeIcon icon={faLayerGroup} />
                    <div>
                      <strong>{t('wsSettings.featTeams')}</strong>
                      <span>{t('wsSettings.featTeamsDesc')}</span>
                    </div>
                  </div>
                </div>

                <div className="wss-convert-note">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <span>{t('wsSettings.convertNote')}</span>
                </div>

                <button className="wss-btn-primary wss-convert-action" onClick={() => setShowConvertModal(true)}>
                  <FontAwesomeIcon icon={faBuilding} /> {t('wsSettings.convertNow')}
                </button>
              </div>
            </div>
          )}

          {/* Danger Zone */}
          {activeTab === 'danger' && isOwner && (
            <div className="wss-section wss-danger-section">
              <div className="wss-section-head danger">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <h2>{t('wsSettings.dangerZone')}</h2>
              </div>

              {/* Transfer Ownership */}
              {isOrg && members.length > 1 && (
                <div className="wss-danger-card">
                  <div className="wss-danger-info">
                    <h3>{t('wsSettings.transferOwnership')}</h3>
                    <p>{t('wsSettings.transferDesc')}</p>
                  </div>
                  <button className="wss-btn-warning" onClick={() => setShowTransferModal(true)}>
                    <FontAwesomeIcon icon={faExchangeAlt} /> {t('wsSettings.transfer')}
                  </button>
                </div>
              )}

              {/* Delete */}
              <div className="wss-danger-card delete">
                <div className="wss-danger-info">
                  <h3>{t('wsSettings.deleteWorkspace')}</h3>
                  <p>{t('wsSettings.deleteDesc')}</p>
                </div>
                <button className="wss-btn-danger" onClick={() => setShowDeleteModal(true)}>
                  <FontAwesomeIcon icon={faTrash} /> {t('wsSettings.delete')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="wss-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="wss-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wss-modal-head">
              <h2><FontAwesomeIcon icon={faExchangeAlt} /> {t('wsSettings.transferOwnership')}</h2>
              <button onClick={() => setShowTransferModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="wss-modal-body">
              <div className="wss-transfer-warn">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <div>
                  <strong>{t('wsSettings.cannotBeUndone')}</strong>
                  <p>{t('wsSettings.transferWarn')}</p>
                </div>
              </div>
              <div className="wss-form-group">
                <label>{t('wsSettings.newOwner')}</label>
                <select value={transferUserId} onChange={(e) => setTransferUserId(e.target.value)}>
                  <option value="">{t('wsSettings.selectMember')}</option>
                  {members.filter((m: any) => m.role !== 'OWNER').map((m: any) => (
                    <option key={m.userId || m.user?.id} value={m.userId || m.user?.id}>
                      {m.user?.firstName} {m.user?.lastName} — {m.user?.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="wss-modal-foot">
              <button className="wss-btn-secondary" onClick={() => setShowTransferModal(false)}>{t('wsSettings.cancel')}</button>
              <button className="wss-btn-warning" onClick={handleTransferOwnership} disabled={!transferUserId || transferring}>
                {transferring ? t('wsSettings.transferring') : t('wsSettings.transferOwnership')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Workspace Modal */}
      {showDeleteModal && (
        <div className="wss-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="wss-modal wss-modal-danger" onClick={(e) => e.stopPropagation()}>
            <div className="wss-modal-head">
              <h2><FontAwesomeIcon icon={faTrash} /> {t('wsSettings.deleteWorkspace')}</h2>
              <button onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="wss-modal-body">
              <div className="wss-delete-warn">
                <div className="wss-delete-icon"><FontAwesomeIcon icon={faTrash} /></div>
                <h3>{t('wsSettings.cannotBeUndone')}</h3>
                <p>
                  {t('wsSettings.deleteWarning')} <strong>{workspace.name}</strong>
                  {workspace._count?.items ? `, ${workspace._count.items} ${t('wsSettings.items').toLowerCase()}` : ''}
                  {workspace._count?.categories ? `, ${workspace._count.categories} ${t('wsSettings.categories').toLowerCase()}` : ''}.
                </p>
              </div>
              <div className="wss-form-group">
                <label>{t('wsSettings.typeToConfirm')} <strong>{workspace.name}</strong></label>
                <input
                  type="text"
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  placeholder={workspace.name}
                />
              </div>
            </div>
            <div className="wss-modal-foot">
              <button className="wss-btn-secondary" onClick={() => setShowDeleteModal(false)}>{t('wsSettings.cancel')}</button>
              <button className="wss-btn-danger" onClick={handleDeleteWorkspace} disabled={deleteConfirmName !== workspace.name || deleting}>
                <FontAwesomeIcon icon={faTrash} /> {deleting ? t('wsSettings.deleting') : t('wsSettings.deletePermanently')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert to Organization Modal */}
      {showConvertModal && (
        <div className="wss-overlay" onClick={() => setShowConvertModal(false)}>
          <div className="wss-modal wss-modal-convert" onClick={(e) => e.stopPropagation()}>
            <div className="wss-modal-head">
              <h2><FontAwesomeIcon icon={faBuilding} /> {t('wsSettings.convertToOrg')}</h2>
              <button onClick={() => setShowConvertModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="wss-modal-body">
              <div className="wss-convert-modal-hero">
                <div className="wss-convert-modal-icon"><FontAwesomeIcon icon={faBuilding} /></div>
                <h3>{t('wsSettings.upgradeTitle')}</h3>
                <p>{t('wsSettings.upgradeDesc')}</p>
              </div>
              <div className="wss-convert-note">
                <FontAwesomeIcon icon={faCheck} />
                <span>{t('wsSettings.convertNote')}</span>
              </div>
            </div>
            <div className="wss-modal-foot">
              <button className="wss-btn-secondary" onClick={() => setShowConvertModal(false)}>{t('wsSettings.cancel')}</button>
              <button className="wss-btn-primary" onClick={handleConvertToOrg} disabled={converting}>
                <FontAwesomeIcon icon={faBuilding} /> {converting ? t('wsSettings.converting') : t('wsSettings.convertNow')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceSettings;
